import React, { useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Popover,
  IconButton,
  Stack,
  Chip,
  Grid
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { isValidHexColor } from '../../utils/themeUtils';

interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  presetColors?: string[];
  disabled?: boolean;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  label,
  value,
  onChange,
  presetColors = [
    '#1976d2', '#dc004e', '#388e3c', '#f57c00', '#7b1fa2', 
    '#0277bd', '#689f38', '#f9a825', '#c2185b', '#5d4037',
    '#455a64', '#e64a19', '#00796b', '#fbc02d', '#8e24aa'
  ],
  disabled = false
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tempColor, setTempColor] = useState(value);
  const [textValue, setTextValue] = useState(value);

  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setTempColor(value);
      setTextValue(value);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTempColor(value); // Reset to original value
    setTextValue(value);
  };

  const handleApply = () => {
    if (isValidHexColor(tempColor)) {
      onChange(tempColor);
      setAnchorEl(null);
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setTextValue(newValue);
    
    if (isValidHexColor(newValue)) {
      setTempColor(newValue);
    }
  };

  const handlePresetClick = (color: string) => {
    setTempColor(color);
    setTextValue(color);
  };

  return (
    <>
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        
        <Button
          variant="outlined"
          onClick={handleClick}
          disabled={disabled}
          startIcon={<PaletteIcon />}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            width: '100%',
            p: 1.5,
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              backgroundColor: value,
              border: '2px solid',
              borderColor: 'divider',
              mr: 1,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {value.toUpperCase()}
          </Typography>
        </Button>
      </Box>

      <Popover
        open={isOpen && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { p: 2, width: 300 }
        }}
      >
        <Paper elevation={0}>
          <Stack spacing={2}>
            {/* Color Picker */}
            <Box>
              <HexColorPicker 
                color={tempColor} 
                onChange={setTempColor}
                style={{ width: '100%', height: 200 }}
              />
            </Box>

            {/* Text Input */}
            <TextField
              label="Hex Color"
              value={textValue}
              onChange={handleTextChange}
              size="small"
              fullWidth
              error={!isValidHexColor(textValue)}
              helperText={!isValidHexColor(textValue) ? 'Invalid hex color' : ''}
              InputProps={{
                sx: { fontFamily: 'monospace' }
              }}
            />

            {/* Preview */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Preview:</Typography>
              <Box
                sx={{
                  width: 40,
                  height: 24,
                  borderRadius: 1,
                  backgroundColor: isValidHexColor(tempColor) ? tempColor : '#transparent',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                {tempColor.toUpperCase()}
              </Typography>
            </Box>

            {/* Preset Colors */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Preset Colors
              </Typography>
              <Grid container spacing={0.5}>
                {presetColors.map((color) => (
                  <Grid item key={color}>
                    <IconButton
                      onClick={() => handlePresetClick(color)}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: color,
                        border: '2px solid',
                        borderColor: tempColor === color ? 'primary.main' : 'divider',
                        '&:hover': {
                          backgroundColor: color,
                          opacity: 0.8,
                        },
                      }}
                    >
                      {tempColor === color && (
                        <CheckIcon 
                          sx={{ 
                            fontSize: 16, 
                            color: color === '#ffffff' ? 'black' : 'white' 
                          }} 
                        />
                      )}
                    </IconButton>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                size="small"
                startIcon={<CloseIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleApply}
                size="small"
                startIcon={<CheckIcon />}
                disabled={!isValidHexColor(tempColor)}
              >
                Apply
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Popover>
    </>
  );
};

export default ColorPicker;